import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, PaletteUtils } from '@shared/utils';
import { TransferRecipientTypeEnum, type RecipientResponse } from '@shared/api';
import { Item } from './recipientDetailsItems';
import { DeleteRecipientModal, EditRecipientModal } from './modals';
import { Button, Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';
import SettingIcon from '@assets/icons/setting.svg';
import EditIcon from '@assets/icons/edit.svg';
import TrashIcon from '@assets/icons/trash.svg';

interface RecipientsDetailsProps {
  recipient: RecipientResponse;
}

export const RecipientsDetails = (props: RecipientsDetailsProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const { t } = useTranslation('recipient');

  const isIndividual = props.recipient.type === TransferRecipientTypeEnum.Individual;
  const isBusiness = props.recipient.type === TransferRecipientTypeEnum.Business;
  const requisites = useMemo(
    () => [
      {
        name: t('details.fields.type'),
        value: props.recipient.type ? t(`recipientType.${props.recipient.type.toLowerCase()}`, { ns: 'common' }) : '-',
      },
      { name: t('details.fields.name'), value: props.recipient.firstName || '-' },
      { name: t('details.fields.lastName'), value: props.recipient.lastName || '-' },
      { name: t('details.fields.idNumber'), value: props.recipient.idNumber || '-' },
      { name: t('details.fields.accountNumber'), value: props.recipient.accountNumber || '-' },
      { name: t('details.fields.companyName'), value: props.recipient.companyName || '-' },
      {
        name: t('details.fields.accountType'),
        value: props.recipient.accountType ? t(`transferRecipientAccountType.${props.recipient.accountType.toLowerCase()}`, { ns: 'common' }) : '-',
      },
      { name: t('details.fields.ibanNumber'), value: props.recipient.iban || '-' },
      { name: t('details.fields.bankName'), value: props.recipient.bankName || '-' },
      { name: t('details.fields.bankProvince'), value: props.recipient.bankProvince || '-' },
      { name: t('details.fields.bankAddress'), value: props.recipient.bankAddress || '-' },
      { name: t('details.fields.bankCity'), value: props.recipient.bankCity || '-' },
      { name: t('details.fields.branchCode'), value: props.recipient.branchCode || '-' },
      { name: t('details.fields.bankCountry'), value: props.recipient.bankCountryCode || '-' },
      { name: t('details.fields.bicSwiftCode'), value: props.recipient.bic || '-' },
      {
        name: t(
          `details.fields.${props.recipient.type === TransferRecipientTypeEnum.Individual ? 'beneficiaryResidentialCountry' : 'incorporationCountry'}`,
        ),
        value: props.recipient.countryCode || '-',
      },
      { name: t('details.fields.currency'), value: props.recipient.currency || '-' },
      { name: t('details.fields.email'), value: props.recipient.email || '-' },
      { name: t('details.fields.address'), value: props.recipient.address || '-' },
      { name: t('details.fields.postCode'), value: props.recipient.postCode || '-' },
      { name: t('details.fields.townCity'), value: props.recipient.city || '-' },
      { name: t('details.fields.provinceRegion'), value: props.recipient.province || '-' },
    ],
    [props.recipient, t],
  );
  return (
    <div
      className={cn(
        'mb-10 flex flex-col bg-white px-6 py-3 dark:bg-soft-black',
        PaletteUtils.isVcfcPalette && 'rounded-xl dark:rounded-xl dark:border dark:border-gray-black',
      )}
    >
      <div
        className={cn(
          'flex min-h-12 cursor-pointer items-center px-0 py-4 transition-colors duration-200',
          'hover:bg-smoky-white dark:hover:bg-transport-black',
        )}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div
          className={cn(
            'mr-4 flex flex-none items-center justify-center text-slate transition-colors duration-200 dark:text-white',
            PaletteUtils.isInsigniaPalette && [isOpen && 'text-primary dark:text-primary'],
          )}
        >
          <Icon icon={<SettingIcon />} color='inherit' />
        </div>
        <Text className='flex-auto'>{t('details.title')}</Text>
        <div className={cn('flex items-center justify-center transition-transform duration-200', isOpen && 'rotate-180')}>
          <Icon className='-rotate-90' icon={<ChevronIcon />} />
        </div>
      </div>
      <div className={cn('transition-all duration-200', isOpen ? 'opacity-100' : 'max-h-0 overflow-hidden')}>
        {requisites
          .filter((item) => {
            if (isIndividual && [t('details.fields.companyName')].includes(item.name)) return false;
            if (isBusiness && [t('details.fields.name'), t('details.fields.lastName'), t('details.fields.idNumber')].includes(item.name))
              return false;
            return true;
          })
          .map((item, idx) => (
            <Item key={idx} name={item.name} value={item.value} />
          ))}
        <Button color='outlined' className='mt-4' fullWidth onClick={() => setShowEditModal(true)}>
          <Icon icon={<EditIcon width='22' height='22' />} color='inherit' />
          {t('details.buttons.editRecipientsDetails')}
        </Button>
        <Button color='outlined' className='mt-4' fullWidth onClick={() => setShowDeleteModal(true)}>
          <Icon icon={<TrashIcon width='22' height='22' />} color='inherit' />
          {t('details.buttons.deleteRecipient')}
        </Button>
        <EditRecipientModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} data={props.recipient} />
        <DeleteRecipientModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} id={props.recipient.id} />
      </div>
    </div>
  );
};
